import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_RESERVATION_MINUTES,
  GUEST_SOURCES,
  RESERVATION_CHANNELS,
  RESERVATION_CHANNEL_LABELS,
  type GuestSource,
  type Reservation,
  type ReservationChannel,
  type RestaurantTable,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { Button, Field, Input, Modal, Select, Textarea } from './ui';

/** YYYY-MM-DD and HH:mm of an instant, on this device's clock (the restaurant's). */
export function localDateTime(iso: string): { date: string; time: string } {
  const value = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`,
    time: `${pad(value.getHours())}:${pad(value.getMinutes())}`,
  };
}

export function todayLocal(): string {
  return localDateTime(new Date().toISOString()).date;
}

const DURATIONS = [60, 90, 120, 150, 180, 240];

/** Take a new booking, or change one that is still to arrive. */
export function BookingDialog({
  open,
  booking,
  defaultDate,
  tables,
  onClose,
}: {
  open: boolean;
  /** Null for a new booking. */
  booking: Reservation | null;
  defaultDate: string;
  tables: RestaurantTable[];
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    guestName: '',
    phone: '',
    partySize: '2',
    date: defaultDate,
    time: '19:00',
    durationMinutes: String(DEFAULT_RESERVATION_MINUTES),
    tableId: '',
    channel: 'PHONE' as ReservationChannel,
    guestSource: '' as GuestSource | '',
    notes: '',
  });

  useEffect(() => {
    if (!open) return;
    if (booking) {
      const { date, time } = localDateTime(booking.reservedAt);
      setForm({
        guestName: booking.guestName,
        phone: booking.phone ?? '',
        partySize: String(booking.partySize),
        date,
        time,
        durationMinutes: String(booking.durationMinutes),
        tableId: booking.tableId ?? '',
        channel: booking.channel,
        guestSource: booking.guestSource ?? '',
        notes: booking.notes ?? '',
      });
    } else {
      setForm((current) => ({
        ...current,
        guestName: '',
        phone: '',
        partySize: '2',
        date: defaultDate,
        tableId: '',
        channel: 'PHONE',
        guestSource: '',
        notes: '',
      }));
    }
  }, [open, booking, defaultDate]);

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const partySize = Number(form.partySize);
  const valid = form.guestName.trim().length > 0 && Number.isInteger(partySize) && partySize >= 1 && form.date && form.time;
  const table = tables.find((candidate) => candidate.id === form.tableId);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        guestName: form.guestName.trim(),
        phone: form.phone.trim() || null,
        partySize,
        reservedAt: new Date(`${form.date}T${form.time}`).toISOString(),
        durationMinutes: Number(form.durationMinutes),
        tableId: form.tableId || null,
        channel: form.channel,
        guestSource: form.guestSource || null,
        notes: form.notes.trim() || null,
      };
      return booking ? api.reservations.update(booking.id, body) : api.reservations.create(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reservations'] });
      toast.success(booking ? 'Booking updated.' : 'Booking added.');
      onClose();
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save the booking.'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={booking ? 'Edit booking' : 'New booking'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!valid} onClick={() => save.mutate()}>
            {booking ? 'Save changes' : 'Add booking'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Guest name">
          <Input autoFocus value={form.guestName} onChange={set('guestName')} placeholder="Nguyễn Văn An" />
        </Field>
        <Field label="Phone" hint="Used to recognise returning guests.">
          <Input inputMode="tel" value={form.phone} onChange={set('phone')} placeholder="0901 234 567" />
        </Field>
        <Field label="Guests">
          <Input inputMode="numeric" value={form.partySize} onChange={set('partySize')} />
        </Field>
        <Field label="Channel">
          <Select value={form.channel} onChange={set('channel')}>
            {RESERVATION_CHANNELS.map((channel) => (
              <option key={channel} value={channel}>
                {RESERVATION_CHANNEL_LABELS[channel]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Date">
          <Input type="date" value={form.date} onChange={set('date')} />
        </Field>
        <Field label="Time">
          <Input type="time" step={900} value={form.time} onChange={set('time')} />
        </Field>
        <Field label="Hold the table for">
          <Select value={form.durationMinutes} onChange={set('durationMinutes')}>
            {DURATIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {minutes % 60 === 0 ? `${minutes / 60} h` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Table (optional)"
          error={table && partySize > table.capacity ? `Table ${table.number} seats ${table.capacity}.` : undefined}
        >
          <Select value={form.tableId} onChange={set('tableId')}>
            <option value="">Decide at the door</option>
            {tables.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                Table {candidate.number} · seats {candidate.capacity}
                {candidate.section ? ` · ${candidate.section}` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="How did they find us? (optional)">
          <Select value={form.guestSource} onChange={set('guestSource')}>
            <option value="">Not recorded</option>
            {GUEST_SOURCES.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Notes (optional)" hint="Occasion, allergies, seating wishes…">
            <Textarea rows={2} value={form.notes} onChange={set('notes')} placeholder="Sinh nhật — bánh kem lúc 20:30" />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

/** The party is here: pick the table (the booked one by default) and open it. */
export function SeatDialog({
  booking,
  tables,
  onClose,
  onSeated,
}: {
  booking: Reservation | null;
  tables: RestaurantTable[];
  onClose: () => void;
  onSeated: (booking: Reservation) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tableId, setTableId] = useState('');

  const free = useMemo(
    () => tables.filter((table) => table.isActive && table.status === 'AVAILABLE'),
    [tables],
  );

  useEffect(() => {
    if (!booking) return;
    const booked = free.find((table) => table.id === booking.tableId);
    setTableId(booked?.id ?? '');
  }, [booking, free]);

  const seat = useMutation({
    mutationFn: () => api.reservations.seat(booking!.id, tableId),
    onSuccess: (seated) => {
      void queryClient.invalidateQueries({ queryKey: ['reservations'] });
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      toast.success(`${seated.guestName} seated at Table ${seated.tableNumber}.`);
      onSeated(seated);
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not seat them.'),
  });

  if (!booking) return null;
  const chosen = tables.find((table) => table.id === tableId);
  const bookedTableBusy = booking.tableId && !free.some((table) => table.id === booking.tableId);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Seat ${booking.guestName}`}
      subtitle={`${booking.partySize} guests${booking.notes ? ` · ${booking.notes}` : ''}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={seat.isPending} disabled={!tableId} onClick={() => seat.mutate()}>
            Seat and open table
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {bookedTableBusy ? (
          <p className="rounded-lg border border-caution/40 bg-caution/10 px-3 py-2 text-sm text-caution">
            Table {booking.tableNumber} is still in use. Choose another free table.
          </p>
        ) : null}
        <Field
          label="Table"
          error={chosen && booking.partySize > chosen.capacity ? `Table ${chosen.number} seats ${chosen.capacity}.` : undefined}
        >
          <Select value={tableId} onChange={(event) => setTableId(event.target.value)}>
            <option value="">Choose a free table</option>
            {free.map((table) => (
              <option key={table.id} value={table.id}>
                Table {table.number} · seats {table.capacity}
                {table.id === booking.tableId ? ' · booked' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <p className="text-xs text-ink-faint">
          Opens the table with {booking.partySize} guests
          {booking.guestSource ? `, source ${booking.guestSource}` : ''}
          {booking.phone ? `, and marks them ${booking.previousVisits > 0 ? 'Returning' : 'New'}` : ''}.
        </p>
      </div>
    </Modal>
  );
}
